import React from "react";
import { useDispatch } from "react-redux";
import MoreIcon from "@mui/icons-material/MoreVert";
import CreateIcon from "@mui/icons-material/Create";
import CancelIcon from "@mui/icons-material/HighlightOff";
import { Box, TextField, Typography } from "@mui/material";
import OkIcon from "@mui/icons-material/CheckCircleOutline";

import EmptyBed from "../../../Svgicons/EmptyBed";
import OccupiedBed from "../../../Svgicons/OccupiedBed";
import { IPD, TriagePriority } from "../../../interfaces/IpdInterface";
import { getDisplayAgeFromDOB } from "../../../helpers/formatters";
import { CalendarFunctions as calFns } from "../../../components/Calendar";
import { getClientGender } from "../../../components/ClientSearch";
import { notificationAdd } from "../../../actions/notification";
import { Client } from "../../../interfaces/ClientInterface";
import { Bed as IBed } from "../../../interfaces/BedInterface";
import { Room } from "../../../interfaces/RoomInterface";
import { usePatchBed } from "../../../hooks/query/useBeds";
import { ExtendedBed } from "../../../hooks/useWardTypeData";

// helper functions
export const formatBedCode = (bed: IBed, rooms: Room[]): string => {
  const bedNameFormat: Array<string> = [];

  const relatedRoom = rooms?.find((room) => Number(room.id) === Number(bed.roomId));

  bedNameFormat.push(relatedRoom?.code || "");
  bedNameFormat.push(bed.number);

  return bedNameFormat.filter(Boolean).join("-");
};

export const getDisplayNameAgeAndGender = (client: Client): string => {
  const ageAndGender = [];
  if (client.dob) {
    ageAndGender.push(getDisplayAgeFromDOB(client.dob));
  }
  if (client.gender) {
    ageAndGender.push(getClientGender(client.gender));
  }
  return `${client.firstName} ${client.lastName} ${ageAndGender.join("/")}`;
};

export const getBedColor = (
  bed: ExtendedBed,
  triagePriorityMap: Record<number, TriagePriority>
): string => {
  if (!bed.isOccupied) return "#1a5fa0";

  const priority = bed.activeIpd?.id ? triagePriorityMap[bed.activeIpd.id] : null;
  if (priority === TriagePriority.Critical) return "#e03030";
  if (priority === TriagePriority.Urgent) return "#e8a020";
  if (priority === TriagePriority.Normal) return "#1a7a4a";
  return "#1a7a4a";
};

// Props
interface BedProps {
  bed: ExtendedBed;
  triagePriorityMap: Record<number, TriagePriority>;
  onMoreIconClick: (props: {
    e: React.MouseEvent<SVGSVGElement>;
    activeBed: ExtendedBed;
    activeIpdOnBed: IPD | null;
  }) => void;
  rooms: Array<Room>;
  patchBed: ReturnType<typeof usePatchBed>["mutate"];
}

// Component
export const Bed = ({
  bed,
  triagePriorityMap,
  onMoreIconClick,
  rooms = [],
  patchBed
}: BedProps): React.ReactNode => {
  const dispatch = useDispatch();

  // local states
  const [hoveredBed, setHoveredBed] = React.useState<{ bedId: number | null }>({ bedId: null });
  const [editingBedDetails, setEditingBedDetails] = React.useState<{
    bedId: number | null;
    customName: string;
  }>({ bedId: null, customName: "" });

  const { activeIpd } = bed;

  // helper functions
  const handleBedUpdate = async (): Promise<void> => {
    if (!editingBedDetails.bedId) {
      dispatch(
        notificationAdd({
          id: new Date().getUTCMilliseconds(),
          variant: "error",
          message: "Missing bed id.",
          autoTimeout: false
        })
      );

      return;
    }

    patchBed(
      {
        id: editingBedDetails.bedId,
        data: { customName: editingBedDetails.customName }
      },
      {
        onSuccess: () => {
          setEditingBedDetails({ bedId: null, customName: "" });
        }
      }
    );
  };

  return (
    <Box
      sx={{
        height: "100%",
        py: 4,
        border: 1,
        borderColor: "divider",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        textAlign: "center",
        borderRadius: "4px",
        position: "relative",

        "&:hover": {
          boxShadow: "0px 15px 30px rgb(0 150 84 / 25%)",
          cursor: "pointer"
        }
      }}
    >
      {/* Bed icon + name + client info */}
      <Box
        px={editingBedDetails?.bedId === bed.id ? 1 : 3}
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          flexGrow: 1,
          gap: 1
        }}
      >
        {bed.isOccupied ? (
          <OccupiedBed color={getBedColor(bed, triagePriorityMap)} />
        ) : (
          <EmptyBed color={getBedColor(bed, triagePriorityMap)} />
        )}

        {/* Editable bed name */}
        <Box
          onMouseEnter={() => setHoveredBed({ bedId: bed.id })}
          onMouseLeave={() => setHoveredBed({ bedId: null })}
          sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 1 }}
        >
          {editingBedDetails?.bedId === bed.id ? (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
              <TextField
                value={editingBedDetails.customName}
                fullWidth
                onChange={(e) =>
                  setEditingBedDetails({ ...editingBedDetails, customName: e.target.value })
                }
                sx={{ marginRight: "10px" }}
              />
              <OkIcon onClick={handleBedUpdate} />
              <CancelIcon onClick={() => setEditingBedDetails({ bedId: null, customName: "" })} />
            </Box>
          ) : (
            <Typography
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                width: "100px"
              }}
            >
              {bed?.customName || formatBedCode(bed, rooms)}
            </Typography>
          )}

          {hoveredBed.bedId && hoveredBed.bedId === bed.id && !editingBedDetails?.bedId && (
            <CreateIcon
              sx={{ fontSize: "15px" }}
              onClick={() =>
                setEditingBedDetails({
                  bedId: bed.id,
                  customName: bed?.customName || formatBedCode(bed, rooms)
                })
              }
            />
          )}
        </Box>

        {/* If Occupied: show client info */}
        {bed.isOccupied && activeIpd?.client && (
          <>
            <Typography
              fontSize="10px"
              sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
            >
              {getDisplayNameAgeAndGender(activeIpd?.client)}
            </Typography>
            <Typography
              fontSize="10px"
              sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
            >
              Admitted at{" "}
              {`${calFns.convertADtoBS(new Date(activeIpd?.dateOfAdmission)).formatted3}`}
            </Typography>
          </>
        )}
      </Box>

      {/* More actions icon */}
      <Box sx={{ position: "absolute", bottom: "5px", right: "5px" }}>
        <MoreIcon
          data-testmation="bedActionButton"
          onClick={(e) => onMoreIconClick({ e, activeBed: bed, activeIpdOnBed: activeIpd })}
          sx={{ cursor: "pointer", marginRight: "5px" }}
        />
      </Box>
    </Box>
  );
};
